import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import Highcharts3D from "highcharts/highcharts-3d";
import HighchartsCylinder from "highcharts/modules/cylinder";
import HighchartsDrilldown from "highcharts/modules/drilldown";
import GridLight from "highcharts/themes/grid-light";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import Box from "@mui/material/Box";
import ChartDatadrawer from "../ChartDatadrawer";
import ChartFallbackImg from "./ChartFallbackImg";

// Initialize Highcharts modules
Highcharts3D(Highcharts);
HighchartsCylinder(Highcharts);
HighchartsDrilldown(Highcharts);
GridLight(Highcharts);

const Stacked3DColumnChart = ({ barchartData }) => {
  const [loading, setLoading] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedSentiment, setSelectedSentiment] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      Highcharts.charts.forEach((chart) => {
        if (chart) chart.reflow();
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  // if (loading) {
  //   return (
  //     <div className="boxes">
  //       <div className="box">
  //         <div></div>
  //         <div></div>
  //         <div></div>
  //         <div></div>
  //       </div>
  //       <div className="box">
  //         <div></div>
  //         <div></div>
  //         <div></div>
  //         <div></div>
  //       </div>
  //       <div className="box">
  //         <div></div>
  //         <div></div>
  //         <div></div>
  //         <div></div>
  //       </div>
  //       <div className="box">
  //         <div></div>
  //         <div></div>
  //         <div></div>
  //         <div></div>
  //       </div>
  //     </div>
  //   );
  // }
  const handleBarClick = (event) => {
    const platform = event.point.category;
    const sentiment = event.point.series.name; // "Negative" or "Neutral"

    setSelectedPlatform(platform);
    setSelectedSentiment(sentiment);
    setIsDrawerOpen(true);
  };

  // if (loading) return <div>Loading...</div>;
  if (!barchartData || barchartData.length === 0)
    return <ChartFallbackImg titleText="Platformwise Sentiment" />;

  const categories = barchartData.map((item) => item._id);
  const unlawfulCounts = barchartData.map(
    (item) => (item.negative || 0) + (item.neutral || 0)
  );

  const subtitleText = categories
    .map((category, index) => `${category}: ${unlawfulCounts[index]}`)
    .join(" , ");

  // Beautiful color palette
  const colors = {
    negative: {
      main: "#FF6B6B", // Coral red
      gradient: {
        linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
        stops: [
          [0, "#FF6B6B"],
          [1, "#ee5253"],
        ],
      },
    },
    neutral: {
      main: "#4ECDC4", // Turquoise
      gradient: {
        linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
        stops: [
          [0, "#4ECDC4"],
          [1, "#45B7AF"],
        ],
      },
    },
  };
  const series = [
    {
      name: "Negative",
      data: barchartData.map(
        (item, index) => (item.negative / unlawfulCounts[index]) * 100
      ),
      stack: "negative",
      color: colors.negative.gradient,
      dataLabels: {
        enabled: true,
        format: "{y:.1f}%",
      },
    },
    {
      name: "Neutral",
      data: barchartData.map(
        (item, index) => (item.neutral / unlawfulCounts[index]) * 100
      ),
      stack: "neutral",
      color: colors.neutral.gradient,
      dataLabels: {
        enabled: true,
        format: "{y:.1f}%",
      },
    },
  ];

  const options = {
    chart: {
      type: "column",
      backgroundColor: "#ffffff",
      options3d: {
        enabled: true,
        alpha: 10,
        beta: 13,
        depth: 47,
        viewDistance: 80,
      },
    },
    credits: { enabled: false },
    title: { text: "Analysis of Recent Data" },
    subtitle: {
      text: subtitleText,
      style: { fontSize: "18px", fontWeight: "bold", color: "#333333" }, // Increase font size here
    },
    xAxis: {
      categories,
      labels: {
        style: { fontWeight: "bold", fontSize: "16px", color: "#024CAA" },
      },
    },
    yAxis: {
      title: { text: null },
      labels: {
        formatter: function () {
          return this.value !== undefined ? `${this.value}%` : ""; // Ensure value exists
        },
      },
      max: 100,
    },
    tooltip: {
      shared: true,
      formatter: function () {
        return this.points
          .map(
            (point) => `
          <span style="color:${point.color}">\u25CF</span> 
          ${point.series.name}: <b>${point.y.toFixed(1)}%</b><br/>
        `
          )
          .join("");
      },
      style: { fontSize: "15px" },
    },
    plotOptions: {
      column: {
        stacking: "normal",
        depth: 70,
        dataLabels: {
          enabled: true,
          style: { fontWeight: "bold", fontSize: "16px", color: "#FFFFFF" },
          format: "{y:.1f}%",
        },
        point: { events: { click: handleBarClick } },
      },
    },
    series,
  };

  return (
    <>
      <div style={{ width: "100%", maxWidth: "1200px", margin: "0 auto" }}>
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
      <SwipeableDrawer
        anchor="right"
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onOpen={() => setIsDrawerOpen(true)} // 🔧 Add this line
        PaperProps={{
          sx: {
            height: "100%",
            overflow: "hidden",
            borderTopLeftRadius: "12px",
            borderBottomLeftRadius: "12px",
          },
        }}
      >
        <Box
          sx={{
            width: 650,
            padding: 2,
            height: "100%",
            overflow: "hidden",
          }}
        >
          {selectedSentiment && selectedPlatform && (
            <ChartDatadrawer
              barsentiment={selectedSentiment}
              platform={selectedPlatform}
            />
          )}
        </Box>
      </SwipeableDrawer>
    </>
  );
};

export default Stacked3DColumnChart;
